import { __ } from "@wordpress/i18n";
import { useEffect, useState } from "react";

// Stores
import { useResult } from "@shared/formContext/hooks/useResult";

// Components
import Box from "../../../../components/Box/Box";
import Cta from "../../../../components/Cta/Cta";
import IconContent from "../../../../components/IconContent/IconContent";
import ChartLegend from "./../../../../components/ChartLegend/ChartLegend";
import ChartPie from "./../../../../components/ChartPie/ChartPie";
import ShareFullLink from "../../../../shared/components/ShareFullLink";

/**
 * Interface for top box display data
 */
interface TopBoxData {
  title: string;
  info: string;
  color: string;
  icon: string;
  content: string;
}

/**
 * Interface for chart data structure based on API response
 */
interface ExpenseBreakdownItem {
  name: string;
  label: string;
  amount: number;
  percentage: number;
  included_in_calculation: boolean;
}

/**
 * Safely converts percentage values to display format
 * Handles both decimal (0.4590) and percentage (45.90) formats from API
 * Returns formatted string with 2 decimal places
 *
 * @param {number} value Percentage value from API
 * @returns {string} Formatted percentage with 2 decimal places (e.g., "66.70")
 */
const safePercentage = (value?: number | null): string => {
  if (value === undefined || value === null || isNaN(value)) return "0.00";

  // If value is less than or equal to 1, assume it's a decimal (0.4590)
  // If value is greater than 1, assume it's already a percentage (45.90)
  const percentageValue = value <= 1 ? value * 100 : value;

  return percentageValue.toFixed(2);
};

/**
 * Status messages based on effort rate thresholds with proper Portuguese localization
 */
const STATUS_MESSAGES = {
  healthy: __(
    "A sua taxa de esforço está em estado saudável. A relação entre os seus rendimentos e os seus encargos mensais está equilibrada e permite-lhe novos investimentos. Contudo, pondere sempre novos gastos com cuidado, resguarde a sua poupança e mantenha o seu orçamento sustentável.",
    "df-simulators",
  ),
  concerning: __(
    "A sua Taxa de Esforço está em estado preocupante. A relação entre os seus rendimentos e as suas despesas pode ser comprometida com alguma facilidade ao subscrever novos créditos. Pondere rever todos os seus encargos e mensalidades, de forma a reduzir as suas obrigações e ganhar folga no seu orçamento mensal.",
    "df-simulators",
  ),
  critical: __(
    "A sua Taxa de Esforço está em estado crítico. É urgente que reveja o seu orçamento em detalhe e reduza os seus encargos financeiros. Considere renegociar o seu crédito habitação, rever as coberturas dos seguros, consolidar créditos ao consumo, bem como despesas com telecomunicações, ginásios e outras mensalidades.",
    "df-simulators",
  ),
  unmanageable: __(
    "O valor das despesas supera o valor dos rendimentos. A sua Taxa de Esforço está em estado crítico. É urgente que reveja o seu orçamento em detalhe e reduza os seus encargos financeiros. Considere renegociar o seu crédito habitação, rever as coberturas dos seguros, consolidar créditos ao consumo, bem como despesas com telecomunicações, ginásios e outras mensalidades.",
    "df-simulators",
  ),
};

/**
 * Props interface for TaxaEsforco component
 */
interface TaxaEsforcoProps {
  ctaTitle?: string;
  ctaText?: string;
}

/**
 * TaxaEsforco Component – Displays effort rate calculation results with proper data from API
 *
 * @param {TaxaEsforcoProps} props Component props
 * @returns {JSX.Element|null} Rendered component or null if no data
 */
const TaxaEsforco: React.FC<TaxaEsforcoProps> = ({ ctaTitle, ctaText }) => {
  const { resultData } = useResult();
  const { response, loading } = resultData;

  const [topBox, setTopBox] = useState<TopBoxData | null>(null);

  useEffect(() => {
    try {
      const data = response?.data?.result?.result;
      const enhancedData = response?.data?.result?.enhanced_data;

      if (!data || !enhancedData) {
        console.warn("[EFFORT RATE] Missing data in useEffect:", {
          hasResultData: !!data,
          hasEnhancedData: !!enhancedData,
        });
        setTopBox(null);
        return;
      }

      const { effort_rate, effort_rate_status } = data;

      // Validate effort_rate is a valid number
      if (typeof effort_rate !== "number" || isNaN(effort_rate)) {
        console.error("[EFFORT RATE] Invalid effort_rate value:", effort_rate);
        setTopBox(null);
        return;
      }

      // Create Portuguese status labels with proper localization
      const statusLabels: Record<string, string> = {
        healthy: __("saudável", "df-simulators"),
        concerning: __("preocupante", "df-simulators"),
        critical: __("crítica", "df-simulators"),
        unmanageable: __("incomportável", "df-simulators"),
      };

      const statusLabel =
        statusLabels[effort_rate_status] || effort_rate_status;
      const title = `${__("Taxa de esforço", "df-simulators")} ${statusLabel}`;
      const info = `${effort_rate}%`;

      // Get the appropriate message for this status
      const content =
        STATUS_MESSAGES[effort_rate_status as keyof typeof STATUS_MESSAGES] ||
        STATUS_MESSAGES.critical;

      // Determine color and icon based on effort rate value
      let color = "green-100";
      let icon = "check";

      if (effort_rate > 100) {
        color = "red-100";
        icon = "error-alt";
      } else if (effort_rate > 60) {
        color = "red-100";
        icon = "error-alt";
      } else if (effort_rate > 30) {
        color = "yellow-100";
        icon = "error-alt";
      }

      setTopBox({ title, info, color, icon, content });
    } catch (error) {
      console.error("[EFFORT RATE] Error processing data in useEffect:", error);
      setTopBox(null);
    }
  }, [response, loading]);

  if (!topBox) {
    console.warn(
      "[EFFORT RATE] No top box data available, component will not render",
    );
    return null;
  }

  // Get effort rate value from the correct path in API response
  let effortRateValue = 0;
  let expenseBreakdown: ExpenseBreakdownItem[] = [];

  try {
    effortRateValue = response?.data?.result?.result?.effort_rate ?? 0;
    expenseBreakdown =
      response?.data?.result?.enhanced_data?.expense_breakdown || [];
  } catch (error) {
    console.error(
      "[EFFORT RATE] Error extracting chart data from response:",
      error,
    );
    // Continue with empty/default values to prevent component crash
  }

  // Process chart data safely
  let chartDataForLegend: any[] = [];
  let chartDataObject: Record<string, number> = {};
  let hasChartData = false;

  try {
    // Validate if we have valid data for charts
    const hasValidChartData =
      Array.isArray(expenseBreakdown) &&
      expenseBreakdown.length > 0 &&
      expenseBreakdown.some(
        (item) =>
          item &&
          typeof item.amount === "number" &&
          !isNaN(item.amount) &&
          item.amount > 0,
      );

    if (hasValidChartData) {
      // Filter out items with zero amounts and prepare data with Portuguese labels
      const validExpenses = expenseBreakdown.filter((item) => {
        return (
          item &&
          typeof item.amount === "number" &&
          !isNaN(item.amount) &&
          item.amount > 0 &&
          typeof item.percentage === "number" &&
          !isNaN(item.percentage) &&
          item.label
        );
      });

      if (validExpenses.length > 0) {
        // Create array format for ChartLegend with Portuguese labels and percentages
        chartDataForLegend = validExpenses.map((item, index) => ({
          name: item.label, // Use Portuguese label from API
          value: safePercentage(item.percentage), // Use safe percentage conversion (returns string)
          color: undefined, // Will be set by chart components
          originalName: item.name, // Keep original name for reference
          amount: item.amount, // Keep amount for reference
        }));

        // Create object format for ChartPie - use Portuguese labels as keys with percentages as numbers
        chartDataObject = validExpenses.reduce(
          (acc, item) => {
            // Use Portuguese label as key with percentage as number for ChartPie
            const percentageValue =
              item.percentage <= 1 ? item.percentage * 100 : item.percentage;
            acc[item.label] = Number(percentageValue.toFixed(2));
            return acc;
          },
          {} as Record<string, number>,
        );

        hasChartData = true;
      }
    } else {
      console.warn("[EFFORT RATE] No valid chart data available:", {
        hasExpenseBreakdown: Array.isArray(expenseBreakdown),
        breakdownLength: expenseBreakdown?.length || 0,
        firstItem: expenseBreakdown?.[0] || null,
      });
    }
  } catch (error) {
    console.error("[EFFORT RATE] Error processing chart data:", error);
    // Reset to safe defaults
    chartDataForLegend = [];
    chartDataObject = {};
    hasChartData = false;
  }

  return (
    <div className="taxa-esforco">
      {/* Main result box */}
      <Box
        className="md:grid-cols-1"
        grid
        loading={loading}
        header={{
          title: topBox.title,
          info: topBox.info,
          color: topBox.color,
        }}
      >
        <IconContent
          icon={topBox.icon}
          title={topBox.title}
          text={topBox.content}
          color={topBox.color}
        />
      </Box>

      {/* Chart section */}
      <Box
        header={{ title: __("Despesas mensais", "df-simulators") }}
        loading={loading}
      >
        {hasChartData ? (
          <div className="flex flex-col md:grid grid-cols-8 gap-8 items-center">
            <div className="col-span-4 relative p-8 md:p-0 w-full">
              <ChartPie
                data={chartDataObject}
                dataManual={false}
                title={__("Taxa de Esforço", "df-simulators")}
                number={`${effortRateValue}%`}
                percentage={true}
              />
            </div>
            <div className="col-span-4 w-full md:w-auto">
              <ChartLegend
                data={chartDataForLegend}
                border={true}
                percentage={true}
                euro_precision={0}
              />
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">
              {__(
                "Não foram encontradas despesas para apresentar no gráfico.",
                "df-simulators",
              )}
            </p>
            <p className="text-sm text-gray-400 mt-2">
              {__(
                "Certifique-se de que preencheu pelo menos uma despesa no formulário.",
                "df-simulators",
              )}
            </p>
          </div>
        )}
      </Box>

      {/* Share Simulation button */}
      <ShareFullLink />

      {/* CTA section */}
      <Cta title={ctaTitle} text={ctaText} />
    </div>
  );
};

export default TaxaEsforco;
